// src/pages/admin/UsersPage.jsx
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, UserCheck, UserX } from 'lucide-react'
import { adminUsersApi } from '../../api/services'
import { Card, CardBody, StatusBadge, Badge, PageSpinner, Pagination, Button } from '../../components/common/ui'
import toast from 'react-hot-toast'

const TABS = [{k:'',l:'All'},{k:'buyer',l:'Buyers'},{k:'supplier',l:'Suppliers'},{k:'admin',l:'Admins'}]
const STATUS = [{k:'',l:'All'},{k:'pending',l:'Pending'},{k:'active',l:'Active'},{k:'suspended',l:'Suspended'}]

export default function UsersPage() {
  const qc = useQueryClient()
  const [role, setRole] = useState('')
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({ queryKey: ['admin-users', role, status, search, page], queryFn: () => adminUsersApi.list({ role: role||undefined, status: status||undefined, search: search||undefined, page }) })
  const approve = useMutation({ mutationFn: id => adminUsersApi.approve(id), onSuccess: () => { toast.success('Approved'); qc.invalidateQueries(['admin-users']) } })
  const suspend = useMutation({ mutationFn: id => adminUsersApi.suspend(id), onSuccess: () => { toast.success('Suspended'); qc.invalidateQueries(['admin-users']) } })

  const users = data?.data?.data?.users?.data || data?.data?.data?.data || []
  const counts = data?.data?.data?.counts || {}
  const lastPage = data?.data?.data?.users?.last_page || data?.data?.data?.last_page || 1

  return (
    <div>
      <h1 className="text-xl font-display font-bold mb-6">Users</h1>

      <div className="flex gap-2 mb-3 flex-wrap">
        {TABS.map(t => <button key={t.k} onClick={() => { setRole(t.k); setPage(1) }} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${role===t.k?'bg-pink text-white border-pink':'border-border text-muted hover:border-pink'}`}>{t.l} {counts[t.k||'all']!=null?`(${counts[t.k||'all']})`:''}</button>)}
        <div className="ml-auto flex gap-1">
          {STATUS.map(s => <button key={s.k} onClick={() => { setStatus(s.k); setPage(1) }} className={`px-3 py-1.5 rounded-full text-xs border transition-all ${status===s.k?'border-pink text-pink bg-pink-light':'border-border text-muted'}`}>{s.l}</button>)}
        </div>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input className="input pl-9 w-full" placeholder="Search by name, email…" value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} />
      </div>

      {isLoading && <PageSpinner />}

      <div className="space-y-2">
        {users.map(u => (
          <Card key={u.id}>
            <CardBody className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm">{u.name}</span>
                  <Badge variant={u.role==='admin'?'purple':u.role==='supplier'?'blue':'grey'}>{u.role}</Badge>
                  <StatusBadge status={u.status || (u.is_active ? 'active' : 'suspended')} />
                  {u.is_verified_supplier && <Badge variant="green">Verified</Badge>}
                </div>
                <p className="text-xs text-muted">{u.email} {u.business_name ? `· ${u.business_name}` : ''}</p>
                <p className="text-xs text-muted">Joined {new Date(u.created_at).toLocaleDateString('en-IN')}</p>
              </div>
              <div className="flex gap-2">
                {u.role==='supplier' && u.status==='pending' && <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate(u.id)}><UserCheck size={13} /> Approve</Button>}
                {u.is_active && u.role!=='admin' && <Button size="sm" variant="danger" loading={suspend.isPending} onClick={() => suspend.mutate(u.id)}><UserX size={13} /></Button>}
                <Link to={`/admin/users/${u.id}`}><Button size="sm" variant="secondary">View →</Button></Link>
              </div>
            </CardBody>
          </Card>
        ))}
        {!isLoading && users.length === 0 && <p className="text-center text-muted py-12">No users found.</p>}
      </div>
      {lastPage > 1 && <div className="mt-6"><Pagination currentPage={page} lastPage={lastPage} onPageChange={setPage} /></div>}
    </div>
  )
}
